import request, { AxiosResponse } from "axios";
import { apiBase } from "../../constants";

import { EmailContentCreate } from "encharge-domain/lib/definitions/api/EmailContentCreate";
import { EmailContentUpdate } from "encharge-domain/lib/definitions/api/EmailContentUpdate";
import { IEmailContent } from "encharge-domain/lib/definitions/IEmailContent";
import { handleForbiddenRead } from "./handleForbidden";
import { IFlow } from "components/Flows/FlowRoute";

/**
 * The revision of each email as this app last loaded or saved it (the
 * API's `revision`: when the email was last saved). The editors send it
 * with their saves, and the API refuses a save over anything saved since
 * (an AI agent's edit, another tab) with 409 EMAIL_CHANGED: before, the
 * editor's autosave sent the whole email it had loaded and put back what
 * the other save had changed.
 */
const loadedRevisions = new Map<number, string>();

/** The revision of an email this app last loaded or saved, if it has one. */
export const loadedEmailRevision = (emailId: number | undefined) =>
  emailId === undefined ? undefined : loadedRevisions.get(emailId);

const keepRevision = (emailId: number | undefined, revision?: string) => {
  if (emailId !== undefined && typeof revision === "string") {
    loadedRevisions.set(emailId, revision);
  }
};

/** What an editor says when it reloads an email saved elsewhere after it opened it. */
export const EMAIL_CHANGED_NOTICE =
  "This email was changed elsewhere (in another tab, by a teammate or through the API) after you opened it. It has been reloaded with those changes. Anything you changed since you opened it, or since your last save, was not saved.";

/** A save refused because the email was saved elsewhere after it was loaded. */
export class EmailChangedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EmailChangedError";
  }
}

export const isEmailChangedError = (e: unknown): e is EmailChangedError =>
  (e as Error)?.name === "EmailChangedError";

/**
 * Emails whose editor save was refused as saved elsewhere, until the editor
 * shows the email as it is now (acceptReloadedEmail): a save still queued
 * with what the editor held is refused here too, so it cannot land with the
 * revision the reload fetched.
 */
const changedEmails = new Set<number>();

/** The editor now shows the email as the API has it: its saves go out again. */
export const acceptReloadedEmail = (emailId: number | undefined) => {
  if (emailId !== undefined) changedEmails.delete(emailId);
};

/**
 * One editor save of an email at a time: the next is sent once the one
 * before it is answered, with the revision that one returned. Sent side by
 * side, the second carried the revision the first replaced and was refused.
 */
const savesInFlight = new Map<number, Promise<unknown>>();
const oneSaveAtATime = <T>(
  emailId: number | undefined,
  save: () => Promise<T>
): Promise<T> => {
  if (emailId === undefined) return save();
  const before = savesInFlight.get(emailId) || Promise.resolve();
  const next = before.catch(() => undefined).then(save);
  savesInFlight.set(emailId, next);
  const done = () => {
    if (savesInFlight.get(emailId) === next) savesInFlight.delete(emailId);
  };
  next.then(done, done);
  return next;
};

/** The editor's save URL, with the revision it loaded when it has one. */
const atLoadedRevision = (url: string, emailId: number | undefined) => {
  if (emailId !== undefined && changedEmails.has(emailId)) {
    throw new EmailChangedError(
      "This email was changed elsewhere after you opened it; it is being reloaded."
    );
  }
  const revision = loadedEmailRevision(emailId);
  return revision ? `${url}?revision=${encodeURIComponent(revision)}` : url;
};

/** The API's refusal of a save over a newer email, as EmailChangedError (and the email held back until reloaded). */
const changedElsewhere = (
  e: any,
  emailId: number | undefined
): EmailChangedError | undefined => {
  if (isEmailChangedError(e)) return e;
  if (
    e?.response?.status !== 409 ||
    e?.response?.data?.error?.errorCode !== "EMAIL_CHANGED"
  ) {
    return undefined;
  }
  if (emailId !== undefined) changedEmails.add(emailId);
  return new EmailChangedError(e.response.data.error.message);
};

/**
 * Whether a failed request left no answer to read: the connection broke, or a gateway gave up (502, 503, 504).
 * The save may well have committed all the same.
 */
const answerWasLost = (e: any): boolean => {
  // (a save held back here, before it was sent, is no lost answer)
  if (isEmailChangedError(e)) return false;
  const status = e?.response?.status;
  return !e?.response || status === 502 || status === 503 || status === 504;
};

/**
 * An editor save whose answer was lost (a gateway's 504 after the API had saved it) is checked against the email
 * as the API has it: when that is what was sent, the save landed, and its revision is the one the editor holds on
 * to. Without that the editor's revision stayed the one before, its next save was refused as "changed elsewhere (for
 * example by an AI agent)", and what was edited since was discarded (L93). The revision is kept only for an email
 * that is the one sent: one an agent changed meanwhile keeps the editor's old revision, so its save is still refused.
 */
const savedAsSent = async (
  sent: EmailContentUpdate
): Promise<{ email: IEmailContent; revision?: string } | undefined> => {
  try {
    const res: AxiosResponse<{
      email: IEmailContent;
      revision?: string;
    }> = await request({
      url: `${apiBase}/v1/emails/${sent.id}`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    const found = res.data.email;
    if (
      found &&
      typeof res.data.revision === "string" &&
      found.html === (sent as any).html &&
      JSON.stringify(found.editor) === JSON.stringify((sent as any).editor)
    ) {
      keepRevision(found.id, res.data.revision);
      return { email: found, revision: res.data.revision };
    }
  } catch (e) {
    // the email could not be read either: the save is reported as it failed
  }
  return undefined;
};

/**
 * An editor's save, one at a time, keeping the revision it returned. One whose answer was lost is read back before
 * the save queued behind it is sent: released when the request failed, that one went out with the revision before
 * and was refused as changed elsewhere (FM1).
 */
const editorSave = (
  email: EmailContentUpdate,
  send: () => Promise<
    AxiosResponse<{ email: IEmailContent; revision?: string }>
  >
) =>
  oneSaveAtATime(email.id, async () => {
    try {
      const res = await send();
      // (only an editor's save moves the revision its editor holds on: see updateEmailFields)
      keepRevision(res.data.email?.id, res.data.revision);
      return res.data;
    } catch (e) {
      if (answerWasLost(e)) {
        const landed = await savedAsSent(email);
        if (landed) return landed;
      }
      throw e;
    }
  });

export const getEmails = async () => {
  try {
    const res: AxiosResponse<{ emails: IEmailContent[] }> = await request({
      url: `${apiBase}/v1/emails`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.emails;
  } catch (e) {
    if (handleForbiddenRead(e.response)) return [];

    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't retrieve your emails. ";
    throw new Error(msg);
  }
};

export const getEmail = async (emailId: IEmailContent["id"]) => {
  try {
    const res: AxiosResponse<{
      email: IEmailContent;
      revision?: string;
    }> = await request({
      url: `${apiBase}/v1/emails/${emailId}`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    keepRevision(res.data.email?.id, res.data.revision);
    return res.data.email;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't retrieve your email. ";
    throw new Error(msg);
  }
};

export const createEmail = async (email: EmailContentCreate) => {
  try {
    const res: AxiosResponse<{
      email: IEmailContent;
      revision?: string;
    }> = await request({
      url: `${apiBase}/v1/emails`,
      method: "POST",
      data: email,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    keepRevision(res.data.email?.id, res.data.revision);
    console.log("response here", res.data);
    return res.data.email;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't create your email. ";
    throw new Error(msg);
  }
};

export const updateEmail = async (
  email: EmailContentUpdate,
  /** an editor's save: refused (EmailChangedError) if the email was saved elsewhere since it loaded it */
  { fromLoadedRevision = false }: { fromLoadedRevision?: boolean } = {}
) => {
  try {
    const url = `${apiBase}/v1/emails/${email.id}`;
    const send = () =>
      request({
        url: fromLoadedRevision ? atLoadedRevision(url, email.id) : url,
        method: "PATCH",
        data: email,
        withCredentials: true,
        validateStatus: (status) => status < 300,
      }) as Promise<AxiosResponse<{ email: IEmailContent; revision?: string }>>;
    if (fromLoadedRevision) return (await editorSave(email, send)).email;
    const res = await send();
    return res.data.email;
  } catch (e) {
    const changed = fromLoadedRevision && changedElsewhere(e, email.id);
    if (changed) throw changed;
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't update your email. ";
    throw new Error(msg);
  }
};

/**
 * Save only the fields given of an email (a broadcast's rename, archiving
 * it): the API merges them into the email as it is when it saves it, so
 * nothing else the store's copy holds (which an AI agent's edit may have
 * made stale) is sent back. The revision an open editor loaded stays as it
 * was: that editor's copy still holds the field as it was, so its next save
 * is refused and it reloads the email, rather than putting the field back.
 */
export const updateEmailFields = async (
  emailId: IEmailContent["id"],
  fields: Partial<Omit<IEmailContent, "id">>
) => {
  try {
    const res: AxiosResponse<{
      email: IEmailContent;
      revision?: string;
    }> = await request({
      url: `${apiBase}/v1/emails/${emailId}`,
      method: "PATCH",
      data: { ...fields, id: emailId },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.email;
  } catch (e) {
    const error = e as any;
    // If we have a specific error message from server throw it.
    const msg =
      error?.response?.data?.error?.message ||
      error?.message ||
      "We couldn't update your email. ";
    throw new Error(msg);
  }
};

export const updateEmailLatestVersion = async (email: EmailContentUpdate) => {
  try {
    // an editor's autosave: refused if the email was saved elsewhere since it loaded it
    return await editorSave(email, () =>
      request({
        url: atLoadedRevision(
          `${apiBase}/v1/emails/${email.id}/versions/latest`,
          email.id
        ),
        method: "PATCH",
        data: email,
        withCredentials: true,
        validateStatus: (status) => status < 300,
      })
    );
  } catch (e) {
    const changed = changedElsewhere(e, email.id);
    if (changed) throw changed;
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't auto-save your email. ";
    throw new Error(msg);
  }
};

export const applyEmailVersion = async (emailId: number, version: number) => {
  try {
    const res: AxiosResponse<{
      email: IEmailContent;
      restoredFromVersion: number;
      revision?: string;
    }> = await request({
      url: `${apiBase}/v1/emails/${emailId}/versions/${version}/restore`,
      method: "POST",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    keepRevision(res.data.email?.id, res.data.revision);
    return res.data;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't apply this version. ";
    throw new Error(msg);
  }
};

export const EMAIL_VERSIONS_PAGE_SIZE = 50;

/** A page of an email's versions, newest first: their details, without the email's content (see getEmailVersion). */
export const getEmailVersions = async (emailId: number, offset = 0) => {
  try {
    const res: AxiosResponse<{ versions: any[] }> = await request({
      url: `${apiBase}/v1/emails/${emailId}/versions`,
      params: { limit: EMAIL_VERSIONS_PAGE_SIZE, offset },
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.versions;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't load version history. ";
    throw new Error(msg);
  }
};

/** One version of an email with its content, to preview or restore it. */
export const getEmailVersion = async (emailId: number, version: number) => {
  try {
    const res: AxiosResponse<{ version: any }> = await request({
      url: `${apiBase}/v1/emails/${emailId}/versions/${version}`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.version;
  } catch (e) {
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't load this version. ";
    throw new Error(msg);
  }
};

export const sendTestEmail = async (
  email: Partial<IEmailContent>,
  sendTo: string,
  /**
   * Render the merge tags as this contact sees them. The mail still goes to
   * the logged-in team member; the server ignores sendTo when this is set.
   */
  mergeDataPersonId?: string
) => {
  try {
    const res: AxiosResponse<{
      success: boolean;
      message: string;
      // Addresses it did not send to (e.g. over the daily test-send limit)
      // while it sent to the others. The message names both.
      notSent?: { email: string; message?: string }[];
    }> = await request({
      url: `${apiBase}/v1/emails/test`,
      method: "POST",
      data: { email, sendTo, mergeDataPersonId },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "Error while sending your email. ";
    throw new Error(msg);
  }
};

export const copyEmailToAnotherAccount = async (data: {
  emailId: IEmailContent["id"];
  targetAccountId: IAccount["id"];
}) => {
  try {
    const res: AxiosResponse<{ email: IEmailContent }> = await request({
      url: `${apiBase}/v1/emails/duplication`,
      method: "POST",
      data,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.email;
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't copy your email. ";
    throw new Error(msg);
  }
};
